import { ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { Box, Button, CircularProgress, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import PayloadEditor from './PayloadEditor'
import { com, kotlin } from '@eidu/web'
import { useSnackBar } from '../../../../components/feedback/SnackBarProvider'
import PermissionError from '../../PermissionError'
import EntityFieldInputGroup from '../shared/EntityFieldInputGroup'
import ScrollFadeContainer from '../../../../components/layout/ScrollFadeContainer'
import PersistentTabs, { PersistentTab } from '../../../../components/layout/PersistentTabs'
import ResizableSplit from '../../../../components/layout/ResizableSplit'
import PageLayout from '../shared/PageLayout'
import useEntityEdit from '../shared/useEntityEdit'
import { KindEditPageProps } from '../KindPages'
import { getEntityLinkFromResourceKey, getResourceKeyFromEntity } from '../../../../io/entityResourceKeyLink'
import { useModifyEntity } from '../../../../io/useEntityMutations'
import { draftFieldValuesToFieldValues } from '../../../../domain/entity/DraftFieldValue'
import Link from '../../../../domain/entity/Link'
import computeLabel from '../shared/computeLabel'
import useUploadResource from '../../../../io/useUploadResource'
import useInitialPayload from './useInitialPayload'
import EntityWithLabelAndRelated from '../../../../domain/entity/EntityWithLabelAndRelated'
import RuntimeSelector from './RuntimeSelector'
import PayloadDocumentationButton from './PayloadDocumentation'
import useRuntimeMetadata from '../../../../io/useRuntimeMetadata'
import { AssetPreviewView, useLocalRuntimes } from './AssetPreviewView'
import LogPanel, { useLogEntries } from './LogPanel'
import { getKnownKindKey } from '../../../../domain/kindRegistry'
import { getEntityLinkFromRuntimeRef, getRuntimeRefFromEntity } from '../../../../io/entityRuntimeIdLink'
import { formatRuntimeRef, RuntimeRef } from '../../../../io/runtimeRef'
import Entity = com.eidu.sharedlib.entity.Entity
import EntityId = com.eidu.sharedlib.entity.EntityId
import entityIdFromString = com.eidu.sharedlib.entity.entityIdFromString
import KtMap = kotlin.collections.KtMap
import assetRuntimeLinkKey = com.eidu.sharedlib.entity.assetRuntimeLinkKey
import assetResourceLinkKey = com.eidu.sharedlib.entity.assetResourceLinkKey

type AssetEditTab = 'payload' | 'fields'

export type RenderPayloadEditorArgs = {
  value: string
  onChange: (value: string) => void
  editable: boolean
  jsonSchema?: Record<string, unknown>
}

/**
 * Lets a specific kind (e.g. Form) reuse the asset edit page while swapping the raw-text payload editor for a bespoke
 * widget and plugging extra read/write behavior into the existing submit flow. All fields are optional; omitting the
 * config yields the generic raw-payload asset behavior.
 */
export type AssetPayloadConfig = {
  renderPayloadEditor?: (args: RenderPayloadEditorArgs) => ReactNode
  // Fallback used only when the entity has no asset.resource link yet; returns the initial payload to seed the editor.
  seedContent?: () => Promise<string | undefined>
  // ANDed into the save-enabled check (e.g. form-level validation errors).
  extraValid?: boolean
  // Throwing pre-write gate run after the entity is created/modified.
  validatePayload?: (payload: string) => void
  // Extra links written in a follow-up patch after create/modify (e.g. form-service contentId/hash).
  buildExtraLinks?: (args: { entityId: EntityId; payload: string }) => Promise<Link[]>
  payloadExtension?: string
  payloadLabel?: string
}

const AssetEditForm = ({
  entity,
  entityId,
  entityType,
  entityTypes,
  loading,
  config,
  initialText,
}: KindEditPageProps & { config?: AssetPayloadConfig; initialText: string }) => {
  const {
    fields,
    updateField,
    submitting,
    valid,
    submitEntity,
    deleteEntity,
    hasPermissions,
    permissionsLoading,
    registerValidation,
    registerPreSubmit,
    registerPostSubmit,
  } = useEntityEdit({ entity, entityId, entityType })

  const { upload } = useUploadResource()
  const { showSnackBar } = useSnackBar()
  const modifyMutation = useModifyEntity()

  // Config is rebuilt by wrappers on each render; a ref keeps the submit callbacks stable without re-registering.
  const configRef = useRef(config)
  configRef.current = config

  const existingResourceKey = entity ? getResourceKeyFromEntity(entity) : undefined

  const existingRuntimeRef = entity ? getRuntimeRefFromEntity(entity) : undefined

  const [selectedRuntimeRef, setSelectedRuntimeRef] = useState<RuntimeRef | undefined>()
  const [runtimeInitialized, setRuntimeInitialized] = useState(false)

  if (existingRuntimeRef && !runtimeInitialized) {
    setSelectedRuntimeRef(existingRuntimeRef)
    setRuntimeInitialized(true)
  }

  const selectedRuntimeId = selectedRuntimeRef ? entityIdFromString(selectedRuntimeRef.entityId) : undefined
  const { jsonSchema, documentation } = useRuntimeMetadata(selectedRuntimeId, getKnownKindKey(entityType.kind))

  // The payload is resolved before this component mounts (see AssetEditPage), so the edit state seeds straight from it.
  const [text, setText] = useState<string>(initialText)
  const textRef = useRef<string>(initialText)
  const [validationErrors, setValidationErrors] = useState<string[]>([])
  const { entries: logEntries, addEntry: addLogEntry, clear: clearLogEntries } = useLogEntries()

  const handleTextChange = (value: string) => {
    setText(value)
    textRef.current = value
  }

  const preSubmit = useCallback(async () => {
    const content = textRef.current
    const links = []

    if (content || existingResourceKey) {
      try {
        const blob = new Blob([content], { type: 'application/octet-stream' })
        const resourceKey = await upload({ data: blob, extension: configRef.current?.payloadExtension ?? 'data' })
        links.push(getEntityLinkFromResourceKey(resourceKey))
      } catch (e) {
        console.error(e)
        showSnackBar('Failed to upload payload', 'error')
        throw e
      }
    }

    // Null link values are only valid for updates (to clear a link), not creates

    if (selectedRuntimeRef) {
      links.push(getEntityLinkFromRuntimeRef(selectedRuntimeRef))
    } else if (entityId) {
      links.push(getEntityLinkFromRuntimeRef(null))
    }

    return links
  }, [upload, showSnackBar, existingResourceKey, selectedRuntimeRef, entityId])

  useEffect(() => {
    registerPreSubmit(preSubmit)
  }, [registerPreSubmit, preSubmit])

  // Gate the payload before anything is persisted; validating in postSubmit would run after the asset.resource is
  // already written, leaving a half-saved entity. Runs in both editor views since it reads the shared payload string.
  useEffect(() => {
    registerValidation(() => {
      const validatePayload = configRef.current?.validatePayload
      if (!validatePayload) return true
      try {
        validatePayload(textRef.current)
        return true
      } catch (e) {
        showSnackBar(e instanceof Error ? e.message : 'Invalid payload', 'error')
        return false
      }
    })
  }, [registerValidation, showSnackBar])

  useEffect(() => {
    registerPostSubmit(async (savedEntityId: EntityId) => {
      const cfg = configRef.current
      const payload = textRef.current
      const extraLinks = cfg?.buildExtraLinks ? await cfg.buildExtraLinks({ entityId: savedEntityId, payload }) : []
      if (extraLinks.length === 0) return
      await modifyMutation.mutateAsync({
        id: savedEntityId,
        patch: {
          valuesToUpdateByFieldId: draftFieldValuesToFieldValues([]),
          linksToUpdate: new Map(extraLinks.map((link) => [link.key, link.value])),
          externalValuesToUpdate: new Map(),
        },
      })
    })
  }, [registerPostSubmit, modifyMutation])

  if (!hasPermissions && !loading && !permissionsLoading) {
    return <PermissionError missingPermittedActions={[]} />
  }

  const [previewEntity, setPreviewEntity] = useState<EntityWithLabelAndRelated | undefined>()

  const buildPreviewEntity = useCallback((): EntityWithLabelAndRelated | undefined => {
    const runtimeLinkValue = formatRuntimeRef(selectedRuntimeRef)
    const base = entity?.entity
    const hasLocalRuntime = useLocalRuntimes && !!getKnownKindKey(entityType.kind)

    if (!runtimeLinkValue && !hasLocalRuntime) return undefined

    const linksMap = base ? new Map(base.links.asJsReadonlyMapView()) : new Map<typeof assetRuntimeLinkKey, string>()
    if (runtimeLinkValue) linksMap.set(assetRuntimeLinkKey, runtimeLinkValue)
    linksMap.set(assetResourceLinkKey, 'Blob:' + encodeURIComponent(textRef.current))

    const modified = new Entity(
      base?.id ?? entityIdFromString(crypto.randomUUID()),
      base?.typeId ?? entityType.id,
      base?.valuesByFieldId ?? KtMap.fromJsMap(new Map()),
      KtMap.fromJsMap(linksMap),
      base?.externalValues ?? KtMap.fromJsMap(new Map())
    )
    return new EntityWithLabelAndRelated(
      modified,
      entity?.primaryLabelText ?? '',
      entity?.secondaryLabelText,
      entity?.referencedEntities ?? [],
      []
    )
  }, [entity, selectedRuntimeRef, entityType])

  useEffect(() => {
    if (!entity) return
    // An existing asset with no payload source has nothing to render from the in-memory editor, so preview the saved
    // entity directly (it renders through its own runtime). With a payload source we preview the editable payload.
    const hasPayloadSource = !!existingResourceKey || !!config?.seedContent
    setPreviewEntity(hasPayloadSource ? buildPreviewEntity() : entity)
  }, [entity])

  const label = computeLabel(entityType, fields)

  const inputPanel = (
    <Stack spacing={2} sx={{ flex: 1, minWidth: 0, minHeight: 0 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ flexShrink: 0 }}>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Typography variant="h6">{config?.payloadLabel ?? 'Payload'}</Typography>
          <PayloadDocumentationButton documentation={documentation} />
        </Stack>
        {useLocalRuntimes && getKnownKindKey(entityType.kind) ? (
          <Typography variant="caption" color="warning.main">
            ⚠️ Local Runtime
          </Typography>
        ) : (
          <RuntimeSelector entityTypes={entityTypes} value={selectedRuntimeRef} onChange={setSelectedRuntimeRef} />
        )}
      </Stack>
      {config?.renderPayloadEditor ? (
        config.renderPayloadEditor({
          value: text,
          onChange: handleTextChange,
          editable: true,
          jsonSchema,
        })
      ) : (
        <PayloadEditor
          value={text}
          onChange={handleTextChange}
          editable={true}
          placeholder="Enter payload..."
          jsonSchema={jsonSchema}
        />
      )}
    </Stack>
  )

  return (
    <PageLayout
      title={label ?? entityType.name}
      subtitle={label ? entityType.name : undefined}
      showCancel
      showSave
      showDelete={!!entity}
      saveDisabled={!(valid && (config?.extraValid ?? true))}
      submitting={submitting}
      onSave={submitEntity}
      onDelete={deleteEntity}
    >
      <Stack sx={{ flex: 1, minHeight: 0 }}>
        {/* New assets need their fields (name etc.) filled in before the payload matters. */}
        <PersistentTabs<AssetEditTab> defaultValue={entity ? 'payload' : 'fields'} sx={{ mb: 1, flexShrink: 0 }}>
          <PersistentTab<AssetEditTab> label={config?.payloadLabel ?? 'Payload'} value="payload">
            <ResizableSplit
              autoSaveId="asset-edit-split"
              expandLabel="Show preview"
              left={inputPanel}
              right={({ collapse }) => (
                <Stack sx={{ flex: 1, minWidth: 0, minHeight: 0 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ flexShrink: 0 }}>
                    <Typography variant="h6">Preview</Typography>
                    <Tooltip title="Hide preview">
                      <IconButton aria-label="hide preview" size="small" onClick={collapse}>
                        <ChevronRightIcon />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                  {previewEntity ? (
                    <AssetPreviewView
                      entity={previewEntity}
                      entityKind={getKnownKindKey(entityType.kind)}
                      entityTypes={entityTypes}
                      onValidationChange={(result) => setValidationErrors(result.valid ? [] : result.errors)}
                      onRefresh={() => {
                        clearLogEntries()
                        setPreviewEntity(buildPreviewEntity())
                      }}
                      onLog={addLogEntry}
                    />
                  ) : (
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {(!selectedRuntimeRef && !(useLocalRuntimes && getKnownKindKey(entityType.kind))) || !text ? (
                        <Typography color="text.secondary">Select a runtime and add a payload to preview</Typography>
                      ) : (
                        <Button variant="outlined" onClick={() => setPreviewEntity(buildPreviewEntity())}>
                          Run Preview
                        </Button>
                      )}
                    </Box>
                  )}
                  {validationErrors.map((error) => (
                    <Typography key={error} variant="caption" color="error">
                      {error}
                    </Typography>
                  ))}
                  {previewEntity && <LogPanel entries={logEntries} onClear={clearLogEntries} />}
                </Stack>
              )}
            />
          </PersistentTab>
          <PersistentTab<AssetEditTab> label="Fields" value="fields">
            <ScrollFadeContainer>
              <EntityFieldInputGroup
                entityType={entityType}
                entityTypes={entityTypes}
                fields={fields}
                updateField={updateField}
                loading={loading}
              />
            </ScrollFadeContainer>
          </PersistentTab>
        </PersistentTabs>
      </Stack>
    </PageLayout>
  )
}

/**
 * Resolves the asset's initial payload, then mounts the editor with it. Splitting the fetch out (rather than seeding
 * editable state in an effect) keeps the edit state a plain useState initializer and avoids the StrictMode-sensitive
 * "seed once" bookkeeping that previously left legacy (form-service) content unseeded.
 */
const AssetEditPage = (props: KindEditPageProps & { config?: AssetPayloadConfig }) => {
  const { entity, entityType, config } = props
  const existingResourceKey = entity ? getResourceKeyFromEntity(entity) : undefined
  const initialText = useInitialPayload({
    seedQueryKey: entity?.entity.id.toString() ?? 'new',
    existingResourceKey,
    seedContent: config?.seedContent,
  })

  if (initialText === undefined) {
    return (
      <PageLayout title={entityType.name} showCancel>
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      </PageLayout>
    )
  }

  return <AssetEditForm {...props} initialText={initialText} />
}

export default AssetEditPage
